import * as React from 'react';
import { Ajax } from '@shared/Ajax';
import { Notify } from '@shared/ui-native/Notify';
import { Button } from '@shared/ui-react/Button';
import { Dialog } from '@shared/ui-react/Dialog';
import { ReactUtils } from '@shared/ui-react/ReactUtils';
import { cs } from '@shared/utils/utils-classname';
import { IMAGE_CLASSES } from '@shared/images/imageClasses';

type ImageAnalytics = NonNullable<IImage['analytics']>;

export const ImageClassesEditDialog = {
    open: (image: IImage, onSaved?: (analytics: ImageAnalytics) => void): void => {
        const targetEl = document.getElementById('react-portal-root');
        ReactUtils.safeRender(
            <Dialog onClose={ImageClassesEditDialog.close} title={__('js_images.classes')}>
                <div className='padded card' style={{ maxWidth: 500 }}>
                    <ImageClassesEditComponent image={image} onSaved={onSaved} />
                </div>
            </Dialog>,
            targetEl
        );
    },

    close: (): void => {
        ReactUtils.safeUnmountComponentAtNode(document.getElementById('react-portal-root'));
    },
};

interface Props {
    image: IImage;
    onSaved?: (analytics: ImageAnalytics) => void;
}

const ImageClassesEditComponent = ({ image, onSaved }: Props): React.ReactElement => {
    const [isLoading, setIsLoading] = React.useState(false);
    const [selected, setSelected] = React.useState<string[]>(() => [...(image.analytics?.imageClasses ?? [])]);

    const toggle = (value: string): void => {
        setSelected(prev => (prev.includes(value) ? prev.filter(v => v !== value) : [...prev, value]));
    };

    const save = async (): Promise<void> => {
        // selected classes get probability 1, previously selected but now deselected classes get 0;
        // the endpoint replaces analytics wholesale, so untouched model probabilities are carried over
        const previouslySelected = image.analytics?.imageClasses ?? [];
        const probabilities: Record<string, number> = { ...(image.analytics?.probabilities ?? {}) };
        selected.forEach(cls => (probabilities[cls] = 1));
        previouslySelected.filter(cls => !selected.includes(cls)).forEach(cls => (probabilities[cls] = 0));

        const analytics: ImageAnalytics = { imageClasses: [...selected], probabilities };

        setIsLoading(true);
        try {
            await Ajax({
                url: `${kuura.getContextPath()}/api/1/image/uuid/${image.uuid}/analytics`,
                method: 'PUT',
                contentType: 'json',
                data: analytics,
            });
        } catch (error) {
            setIsLoading(false);
            Notify.error();
            return;
        }

        setIsLoading(false);
        Notify.success();
        if (typeof onSaved === 'function') {
            onSaved(analytics);
        }
        ImageClassesEditDialog.close();
    };

    return (
        <div className={cs('flex-column', { 'overlay-loading': isLoading })}>
            <div className='of-a' style={{ maxHeight: 400 }}>
                {IMAGE_CLASSES.map(c => (
                    <label key={c.value} className='flex-row ai-center gap-half' style={{ padding: '2px 0' }}>
                        <input type='checkbox' checked={selected.includes(c.value)} onChange={() => toggle(c.value)} />
                        <span>{c.label}</span>
                    </label>
                ))}
            </div>

            <div className='flex-row' style={{ justifyContent: 'space-between', marginTop: 25 }}>
                <Button className='ik-button-secondary--subtle' onClick={ImageClassesEditDialog.close}>
                    {__('general.close')}
                </Button>
                <Button className='ik-button-primary right' onClick={save} disabled={isLoading}>
                    {__('general.save')}
                </Button>
            </div>
        </div>
    );
};
